import { useState, useRef, useContext, useCallback, useEffect } from "react";
import { FluentButton } from "../../../../components/FluentUI/Fluent-Button";
import { Button, Dialog, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, Tooltip } from '@fluentui/react-components';

import FluentUiIcon from "../../../../assets/fluent-ui-icon";
import { useFluentToast } from "../../../../components/FluentUI/Fluent-Toast";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { LoginContext } from "../../../../services/api/authentication-api";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import {
    getLastUpdateTooltip,
    submitButton,
} from "../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import {
    getInitialDetailState,
    saveDetailStateToStorage,
    clearDetailStateFromStorage,
    getMembersTimeLogColumns,
    getDealTrackerModalColumns,
    getDealsData,
    handleStafferTimeLogExport,
} from "../levfin-helper";

import { SaveLevfinBankerDataOnStafferDashboard } from "../../../../services/api/dashboard-api";
import "../levfin-junior-banker/levfin-time-log.scss";
import LevfinBankerDetailGrid from "../levfin-junior-banker/levfin-junior-banker-mydeals/levfin-detail-grid";
import LevfinBankerSummaryGrid from "..//levfin-junior-banker/levfin-junior-banker-mydeals/levfin-summary-grid";
import { FluentTooltip } from "../../../../components/FluentUI/Fluent-Tooltip";
import { FluentBadge } from "../../../../components/FluentUI/Fluent-Badge";

const LevfinMembersTimeLogModal = ({ banker, onHide, show, fetchLevfinStafferTimeLog, setIsLevfinStafferTimeLogLoading }) => {
    const user = useContext(LoginContext);
    const [detailState, setDetailState] = useState(() => getInitialDetailState(user?.userID, "levfinJuniorBankersDealsForStaffer"));
    const [isSaving, setIsSaving] = useState(false);
    const [data, setData] = useState(banker.Deals || []);
    const [searchValue, setSearchValue] = useState("");
    const [summaryItem, setSummaryItem] = useState(banker);
    const [originalOverallCapacity, setOriginalOverallCapacity] = useState(banker?.OverallCapacity ?? null);
    const { showToast, FluentToast } = useFluentToast();
    const _mainExport = useRef(null);
    const _modalExport = useRef(null);
    const searchValueRef = useRef(searchValue);
    const history = useHistory();
    const canCreate = user?.currentUser?.privileges?.["Projects"]?.Add ?? false;
    const canEdit = user?.currentUser?.privileges?.["Projects"]?.Edit ?? false;
    useEffect(() => {
        document.body.classList.toggle("energy-dialog-open", show);
    }, [show]);
    const handleModifySearchFocus = () => {
        const input = document.getElementById("levfin-search-input");
        if (input) {
            input.focus();
        }
    };

    const handleDetailStateChange = (newState) => {
        const userId = user?.userID;
        setDetailState(newState);
        saveDetailStateToStorage(userId, newState, searchValueRef.current, "levfinJuniorBankersDealsForStaffer");
    };

    const handleSubmit = async () => {
        const payload = [{
            UserId: summaryItem.BankerId,
            TaskId: summaryItem.OverallCapacityTaskId,
            OverallCapacity: summaryItem.OverallCapacity,
        }];
        setIsSaving(true);
        let result = await SaveLevfinBankerDataOnStafferDashboard(payload);
        if (result?.status === 200) {
            if (result?.data) {
                showToast("Success", "Overall Capacity submitted successfully", "success");
                setOriginalOverallCapacity(summaryItem.OverallCapacity);
                setSummaryItem((prev) => ({
                    ...prev,
                    LastUpdated: new Date().toUTCString(),
                }));
                setIsLevfinStafferTimeLogLoading(true);
                fetchLevfinStafferTimeLog().then(() => {
                    setIsLevfinStafferTimeLogLoading(false);
                });
            } else {
                showToast("Error", "Unexpected response from server", "error");
            }
        } else {
            showToast("Error", "Failed to submit Overall Capacity", "error");
        }
    }

    const hasCapacityEdit = originalOverallCapacity !== summaryItem?.OverallCapacity;
    const canSubmitCapacity = hasCapacityEdit && Boolean(summaryItem?.OverallCapacityTaskId);
    const isSubmitDisabled = isSaving || !canSubmitCapacity;
    const tooltipText = getLastUpdateTooltip(summaryItem?.LastUpdated);
    const submitBtn = submitButton(isSubmitDisabled, handleSubmit);


    const hasAnyFilter =
        detailState.filter &&
        Array.isArray(detailState.filter.filters) &&
        detailState.filter.filters.length > 0;

    const canReset = detailState.sort.length > 0 || hasAnyFilter;
    const resetDataState = () => {
        setSearchValue("");
        const resetState = { sort: [], filter: null };
        setDetailState(resetState);
        clearDetailStateFromStorage(user?.userID, "levfinJuniorBankerMyDeals");
    };

    const updateSummaryNumbers = (filteredData) => {
        const counts = {};

        // Initialize all category options with 0 (mirrors categoryOptions seeding in C#)
        if (banker.CategoryOptions?.length) {
            banker.CategoryOptions.forEach((option) => {
                if (option?.trim()) {
                    counts[option.trim()] = 0;
                }
            });
        }

        // Count deals per GroupName (mirrors the foreach over deals in C#)
        filteredData.forEach((deal) => {
            const key = deal.GroupName?.trim() || "Uncategorized";
            counts[key] = (counts[key] || 0) + 1;
        });

        // Convert to array of { Key, Value } pairs to match KeyValuePair<string, int>[]
        const countByCategory = Object.entries(counts).map(([key, value]) => ({
            Key: key,
            Value: value,
        }));

        setSummaryItem((prev) => ({
            ...prev,
            Total: filteredData.length,
            NumberOfDeals: countByCategory,
        }));
    };

    const updateOverallCapacityDraft = (newVal) => {
        setSummaryItem((prev) => ({
            ...prev,
            OverallCapacity: newVal,
        }));
    }

    return (
        <>
            <Dialog open={show} modalType="modal" data-testid="deal-resource-modal"
                onOpenChange={(event, data) => {
                    if (!data.open) {
                        onHide();
                    }
                }}>
                <DialogSurface className=" deal-resource-modal levfin-time-log">
                    <DialogBody>
                        <DialogTitle className="d-flex justify-content-between"
                            action={
                                <DialogTrigger action="close">
                                    <Button
                                        appearance="subtle"
                                        aria-label="close"
                                        icon={<FluentUiIcon name="DismissRegular" />}
                                    />
                                </DialogTrigger>
                            }
                        >

                            <span>
                                <FluentBadge
                                    appearance="tint"
                                    size="large"
                                    color={"brand"}
                                    text={banker.GroupName}
                                    shape="rounded"
                                />
                                <FluentBadge
                                    appearance="tint"
                                    size="large"
                                    color={"informative"}
                                    className="ml-2"
                                    text={`${banker.Total} Deal${banker.Total !== 1 ? "s" : ""}`}
                                />
                                <p className="bankerName">{banker.Banker}</p>
                                <p className="subDepartmentTitle">Analysts & Associates/VPs Logs</p>
                            </span>
                            <div className="d-flex align-items-start justify-content-end modal-header-buttons">
                                <Tooltip content="Reset Filters and Sorting">
                                    <FluentButton
                                        appearance="secondaryWithOutline"
                                        icon={
                                            <FluentUiIcon
                                                name="ArrowResetRegular"
                                                color={canReset ? "#0F6CBD" : "#A0A0A0"}
                                                size={20}
                                                disabled={!canReset}
                                            />
                                        }
                                        disabled={!canReset}
                                        onClick={resetDataState}
                                    />
                                </Tooltip>
                                <FluentTooltip content="Export Data" target={
                                    <FluentButton
                                        appearance="secondaryWithOutline"
                                        icon={
                                            <FluentUiIcon
                                                name="ArrowDownloadRegular"
                                                color="#0F6CBD"
                                                size={20}
                                            />
                                        }
                                        onClick={() =>
                                            handleStafferTimeLogExport({ mainRef: _mainExport, modalRef: _modalExport, mainData: [banker], categoryOptions: banker?.CategoryOptions ?? [], fileNameBase: `MembersTimeLog_${banker?.Banker}` })
                                        }
                                    />
                                }>
                                </FluentTooltip>
                                {tooltipText ? (
                                    <Tooltip content={tooltipText} relationship="label" positioning="below">
                                        <div>{submitBtn}</div>
                                    </Tooltip>
                                ) : (
                                    submitBtn
                                )}
                            </div>
                        </DialogTitle>

                        <DialogContent>
                            <div className="detail-grid-scroll-container project-staffer-member-modal">
                                <ExcelExport ref={_mainExport} fileName={`MembersTimeLog_Detail`} data={[banker]}>
                                    {getMembersTimeLogColumns(banker?.CategoryOptions ?? [])}
                                </ExcelExport>
                                <ExcelExport data={getDealsData([banker])} ref={_modalExport}>
                                    {getDealTrackerModalColumns()}
                                </ExcelExport>
                                <LevfinBankerSummaryGrid
                                    summaryItem={summaryItem}
                                    onOverallCapacityChange={(newVal) => { updateOverallCapacityDraft(newVal) }}
                                    fromStafferModal={true}
                                />

                                <LevfinBankerDetailGrid
                                    data={data}
                                    categoryOptions={banker.CategoryOptions}
                                    emptyCategories={banker.EmptyCategories}
                                    canCreate={canCreate}
                                    detailState={detailState}
                                    onDetailStateChange={handleDetailStateChange}
                                    searchValue={searchValue}
                                    resetDataState={resetDataState}
                                    refineSearch={handleModifySearchFocus}
                                    updateSummaryNumbers={updateSummaryNumbers}
                                    fromStaffer={true}
                                />
                                <div
                                    tabIndex={0}
                                    style={{ width: 0, height: 0, overflow: "hidden" }}
                                    onFocus={() => {
                                        const btn = document.getElementById("tb-submit-btn");
                                        if (btn) btn.focus();
                                    }}
                                />
                            </div>
                        </DialogContent>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
};

export default LevfinMembersTimeLogModal;;